/**
 * {order}の訂正版（#233 2b、docs/93 §14.5）の部品：発行前の確認の「前回とのちがい」と、承諾ページの「変更点」の表。
 *   どちらも 合計の増減 → 条件の変更（1行ずつ）→ 明細（変更／追加／削除／計算）の順。印は文字（色だけで伝えない）。
 *   変わったかどうかは DB（fn_revision_diff・fn_order_revision_changes）。ここは並べるだけ
 */
import { COMPARE_MSG, ORDER_REVISE_MSG as M, type OrderTermChange, type RevisionDiffRow } from "@genbaryoku/contract";
import { yen } from "../../lib/num.js";

/** 増減（＋は墨、−は朱。記号を文字で添える） */
function Delta({ d }: { d: number }) {
  if (d === 0) return <span className="muted">±0</span>;
  return (
    <span className={"num" + (d < 0 ? " shu" : "")}>
      （{d > 0 ? "＋" : "−"}
      {yen(Math.abs(d))}円）
    </span>
  );
}

const fieldText = (x: RevisionDiffRow) => x.fields.map((f) => `${f.label ? `${f.label} ` : ""}${f.old || "（空）"} → ${f.new || "（空）"}`).join("、");

/** 前回とのちがい（変更点）。prev＝前回の番号・no＝今回の番号・recordNo＝前回の承諾の記録番号 */
export function OrderChanges({
  prev,
  no,
  prevTotal,
  total,
  recordNo,
  terms,
  rows,
  unchanged,
  testId = "order-changes",
}: {
  prev: string;
  no: string;
  prevTotal: number;
  total: number;
  recordNo: string | null;
  terms: OrderTermChange[];
  rows: RevisionDiffRow[];
  unchanged?: number | undefined;
  testId?: string;
}) {
  const heads = rows.filter((x) => x.kind === "head");
  const lines = rows.filter((x) => x.kind === "line");
  const sheets = rows.filter((x) => x.kind === "sheet");
  const empty = !terms.length && !rows.length;
  return (
    <div className="rvdiff ochanges" data-testid={testId}>
      <div className="rvrow">
        <b data-testid={`${testId}-nos`}>{M.diffLead.replace("{prev}", prev).replace("{no}", no)}</b>
        <span className="muted">{COMPARE_MSG.total}</span>
        <span className="num">
          {yen(prevTotal)} → {yen(total)} 円
        </span>
        <Delta d={total - prevTotal} />
      </div>
      {recordNo ? <div className="rvrow muted" data-testid={`${testId}-record`}>{M.prevAck.replace("{rec}", recordNo)}</div> : null}
      {empty ? (
        <p className="shu" style={{ margin: "6px 0 0" }} data-testid={`${testId}-empty`}>
          {M.noDiff}
        </p>
      ) : null}
      {terms.length || heads.length ? (
        <div>
          <div className="rvh">{M.terms}</div>
          {terms.map((x) => (
            <div className="rvrow" key={x.key} data-testid={`${testId}-term`} data-key={x.key}>
              <span>{x.label}</span>
              <span className="muted" style={{ whiteSpace: "pre-wrap" }}>{x.old ? `${x.old} → ${x.new}` : x.new}</span>
            </div>
          ))}
          {heads.map((x, i) => (
            <div className="rvrow" key={`h${i}`} data-testid={`${testId}-head`}>
              <span>{x.name}</span>
              <span className="muted" style={{ whiteSpace: "pre-wrap" }}>{fieldText(x)}</span>
            </div>
          ))}
        </div>
      ) : null}
      {lines.length ? (
        <div>
          <div className="rvh">{M.lines}</div>
          {lines.map((x, i) => (
            <div className="rvrow" key={i} data-testid={`${testId}-line`} data-change={x.change}>
              <span className={"chip " + (x.change === "removed" ? "shu" : x.change === "calc" ? "calc" : "ai")}>{COMPARE_MSG.change[x.change]}</span>
              {x.change === "removed" ? <s>{x.name || "（名前なし）"}</s> : <span>{x.name || "（名前なし）"}</span>}
              {x.sheetName ? <span className="muted">（{x.sheetName}）</span> : null}
              {x.change === "changed" || x.change === "calc" ? (
                <span className="muted">{fieldText(x)}</span>
              ) : x.change === "added" ? (
                <span className="num">{x.newAmount ? yen(x.newAmount) : ""}</span>
              ) : (
                <span className="num">{x.oldAmount ? yen(x.oldAmount) : ""}</span>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {sheets.length ? (
        <div>
          <div className="rvh">{COMPARE_MSG.sheets}</div>
          {sheets.map((x, i) => (
            <div className="rvrow" key={`s${i}`}>
              <span>{x.name || "（名前なし）"}</span>
              <span className="muted">{x.change === "added" ? COMPARE_MSG.change.added : x.change === "removed" ? COMPARE_MSG.change.removed : fieldText(x)}</span>
            </div>
          ))}
        </div>
      ) : null}
      {unchanged && !empty ? <p className="hint" style={{ margin: "6px 0 0" }}>{M.unchanged.replace("{n}", String(unchanged))}</p> : null}
    </div>
  );
}
